import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, FileText, Search as SearchIcon } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { GlobalSearchHit } from '@/api/endpoints/search';
import { HighlightedText } from '@/features/search/components/HighlightedText';
import {
  GLOBAL_SEARCH_MAX_QUERY_LENGTH,
  GLOBAL_SEARCH_MIN_QUERY_LENGTH,
  useGlobalSearch,
} from '@/features/search/hooks/useGlobalSearch';
import { canOpenGlobalSearchHit } from '@/features/search/lib/global-search-result-access';
import {
  GLOBAL_SEARCH_TYPE_ICONS,
  GLOBAL_SEARCH_TYPE_LABELS,
} from '@/features/search/lib/global-search-hit-types';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/cn';
import { useDismissOnOutsidePointerDown } from '@/lib/use-anchored-overlay-position';
import { useAuthStore } from '@/stores/auth.store';

/** Hits shown in the dropdown; the rest are one Enter away on the Search page. */
const PREVIEW_LIMIT = 6;
/** Quiet time before the preview asks the server, so typing a word sends one request. */
const PREVIEW_DEBOUNCE_MS = 250;
const PREVIEW_WIDTH_PX = 416;
const VIEWPORT_GUTTER_PX = 8;

/**
 * Fixed position for the preview: right-aligned under the search box, wider than the box, and
 * kept inside the viewport on a phone where the box sits near the right edge.
 */
function usePreviewPosition(
  open: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
): CSSProperties | null {
  const [style, setStyle] = useState<CSSProperties | null>(null);

  // Layout side effect: measures the anchor and follows viewport resizes.
  useEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }
    function update() {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const width = Math.min(PREVIEW_WIDTH_PX, window.innerWidth - VIEWPORT_GUTTER_PX * 2);
      const left = Math.min(
        Math.max(rect.right - width, VIEWPORT_GUTTER_PX),
        window.innerWidth - VIEWPORT_GUTTER_PX - width,
      );
      setStyle({ position: 'fixed', top: rect.bottom + 4, left, width });
    }
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [anchorRef, open]);

  return style;
}

/**
 * Compact header search control (legacy chrome parity).
 *
 * Enter goes to `/search?q=…`. While typing, a dropdown previews the top hits from
 * `GET /api/search` with the searched words highlighted (FR-517); arrow keys move through
 * them, Enter opens the selected one, Escape closes the preview.
 */
export function HeaderSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = useAuthStore((s) => s.user?.role);
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const formRef = useRef<HTMLFormElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const typed = value.trim();
  const debounced = useDebouncedValue(typed, PREVIEW_DEBOUNCE_MS);
  const showPreview = open && typed.length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH;
  const { data, isError, isFetching } = useGlobalSearch({
    q: debounced,
    limit: PREVIEW_LIMIT,
    enabled: showPreview && debounced.length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH,
  });
  const position = usePreviewPosition(showPreview, formRef);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);
  useDismissOnOutsidePointerDown(showPreview, close, formRef, listboxRef);

  // Tickets has its own list filter in the page header. Showing this one
  // next to it is the "two search bars" the page was reading as.
  if (location.pathname.includes('/admin/roadmap/requests')) {
    return null;
  }

  const hits: GlobalSearchHit[] = data?.items ?? [];
  const terms = data?.terms ?? [];
  const total = data?.total ?? 0;
  // The "See all" row follows the hits as one more option.
  const optionCount = hits.length > 0 ? hits.length + 1 : 0;
  const seeAllIndex = hits.length;
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  const goToSearchPage = (q: string) => {
    const params = new URLSearchParams();
    if (q.length > 0) params.set('q', q);
    params.set('category', 'all');
    params.set('page', '1');
    const target = `/search?${params.toString()}`;
    if (location.pathname === '/search') {
      navigate(target, { replace: true });
    } else {
      navigate(target);
    }
  };

  const openHit = (hit: GlobalSearchHit) => {
    if (!canOpenGlobalSearchHit(userRole, hit) || !hit.link) return;
    close();
    setValue('');
    navigate(hit.link);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (showPreview && activeIndex >= 0 && activeIndex < hits.length) {
      openHit(hits[activeIndex]);
      return;
    }
    close();
    if (typed.length > 0 && typed.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
      navigate('/search');
      return;
    }
    goToSearchPage(typed);
  };

  // A press on an option must not blur the input, so the arrow keys keep working after a click.
  const keepInputFocus = (e: React.MouseEvent) => e.preventDefault();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setOpen(true);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && showPreview) {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    setOpen(true);
    if (optionCount === 0) return;
    setActiveIndex((current) =>
      e.key === 'ArrowDown'
        ? Math.min(current + 1, optionCount - 1)
        : Math.max(current - 1, -1),
    );
  };

  const renderBody = () => {
    if (isError) {
      return (
        <p className="px-3 py-3 text-sm text-muted-foreground">
          Could not load a preview. Press Enter to search.
        </p>
      );
    }
    if (!data) {
      return (
        <p className="px-3 py-3 text-sm text-muted-foreground" role="status">
          Searching…
        </p>
      );
    }
    if (hits.length === 0) {
      return (
        <p className="px-3 py-3 text-sm text-muted-foreground" role="status">
          No matches for &ldquo;{debounced}&rdquo;.
        </p>
      );
    }
    // Options are buttons, as in the other comboboxes; focus stays in the input and the arrow keys
    // move through them there.
    return (
      <div
        id={listboxId}
        role="listbox"
        aria-label="Search preview"
        className={cn('py-1 transition-opacity', isFetching && 'opacity-70')}
      >
        {hits.map((hit, index) => {
          const Icon = GLOBAL_SEARCH_TYPE_ICONS[hit.type] ?? FileText;
          const typeLabel = GLOBAL_SEARCH_TYPE_LABELS[hit.type] ?? hit.type;
          const canOpen = canOpenGlobalSearchHit(userRole, hit);
          return (
            <button
              key={`${hit.type}-${hit.id}`}
              type="button"
              tabIndex={-1}
              id={optionId(index)}
              role="option"
              aria-selected={activeIndex === index}
              aria-disabled={!canOpen}
              onMouseDown={keepInputFocus}
              onClick={() => openHit(hit)}
              onMouseEnter={() => setActiveIndex(index)}
              className={cn(
                'flex w-full items-start gap-3 px-3 py-2 text-left',
                canOpen ? 'cursor-pointer' : 'cursor-default opacity-60',
                activeIndex === index && 'bg-accent text-accent-foreground',
              )}
            >
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                  <HighlightedText text={hit.title} terms={terms} />
                </p>
                {hit.description ? (
                  <p className="truncate text-xs text-muted-foreground">
                    <HighlightedText text={hit.description} terms={terms} />
                  </p>
                ) : null}
              </div>
              <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {typeLabel}
              </span>
            </button>
          );
        })}
        <button
          type="button"
          tabIndex={-1}
          id={optionId(seeAllIndex)}
          role="option"
          aria-selected={activeIndex === seeAllIndex}
          onMouseDown={keepInputFocus}
          onClick={() => {
            close();
            goToSearchPage(typed);
          }}
          onMouseEnter={() => setActiveIndex(seeAllIndex)}
          className={cn(
            'flex w-full cursor-pointer items-center justify-between gap-2 border-t border-border px-3 py-2 text-left text-sm font-medium text-foreground',
            activeIndex === seeAllIndex && 'bg-accent text-accent-foreground',
          )}
        >
          <span className="truncate">
            See all {total} result{total === 1 ? '' : 's'} for &ldquo;{typed}&rdquo;
          </span>
          <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
        </button>
      </div>
    );
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="relative"
      role="search"
      aria-label="Global search"
    >
      <SearchIcon
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <input
        type="search"
        role="combobox"
        value={value}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        maxLength={GLOBAL_SEARCH_MAX_QUERY_LENGTH}
        placeholder="Search…"
        aria-label="Search properties, reports, and work orders"
        aria-autocomplete="list"
        aria-expanded={showPreview}
        aria-controls={showPreview && optionCount > 0 ? listboxId : undefined}
        aria-activedescendant={
          showPreview && activeIndex >= 0 && activeIndex < optionCount
            ? optionId(activeIndex)
            : undefined
        }
        className="h-9 w-36 rounded-md border border-input bg-transparent py-1 pl-8 pr-3 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:w-44 md:w-56"
      />
      {showPreview && position
        ? createPortal(
            <div
              ref={listboxRef}
              style={position}
              className="z-popover max-h-[70vh] overflow-auto overscroll-contain rounded-md border border-border bg-popover text-popover-foreground shadow-md"
            >
              {renderBody()}
            </div>,
            document.body,
          )
        : null}
    </form>
  );
}
